import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";

/**
 * Keys that are bookkeeping / stamps, not policy logic. Ignored when deciding
 * whether Edit & run should fork a derived combined policy.
 *
 * Candle TF on *segments* is kept (multi-TF combine logic). Meta / dual-TF
 * stamps are ignored so date-window / timeframe-only runs do not fork.
 */
const POLICY_LINEAGE_IGNORE_KEYS = new Set([
  "combined_at",
  "segment_count",
  "source_title",
  "segment_kind",
  "resolved_candle_timeframe",
  "source_candle_timeframes",
  "management_candle_timeframe",
  "entry_candle_timeframe",
]);

const COMBINE_OPERAND_KEYS = ["entry_keep_combine", "entry_veto_combine"] as const;

/**
 * Fold top-level combine operands into `meta` and drop the duplicates so a
 * dual-written rebuild matches a meta-only persisted bundle.
 */
function normalizeCombineOperandsForLineage(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...obj };
  const metaRaw = next.meta;
  const meta: Record<string, unknown> =
    metaRaw && typeof metaRaw === "object" && !Array.isArray(metaRaw)
      ? { ...(metaRaw as Record<string, unknown>) }
      : {};

  for (const key of COMBINE_OPERAND_KEYS) {
    const top = next[key];
    const inMeta = meta[key];
    if (inMeta == null && top != null) meta[key] = top;
    delete next[key];
  }

  if (Object.keys(meta).length > 0) next.meta = meta;
  else delete next.meta;
  return next;
}

/** Stable JSON for policy-tree equality (sorted keys, ignore stamp fields). */
export function canonicalizePolicyTreeForLineage(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map((entry) => canonicalizePolicyTreeForLineage(entry));
  }
  let obj = value as Record<string, unknown>;
  // Bundle / policy payloads may dual-write combine operands at top-level + meta.
  if (
    typeof obj.schema_version === "string" ||
    Array.isArray(obj.segments) ||
    obj.root != null ||
    Array.isArray(obj.rules)
  ) {
    obj = normalizeCombineOperandsForLineage(obj);
  }

  const out: Record<string, unknown> = {};
  const isRuleLike = "condition" in obj || "actions" in obj;
  const isSegmentLike =
    ("root" in obj || "rules" in obj || "rule" in obj) && "action" in obj;

  for (const key of Object.keys(obj).sort()) {
    if (POLICY_LINEAGE_IGNORE_KEYS.has(key)) continue;
    // Root / meta candle_timeframe stamps are not logic; segment TF is.
    if (key === "candle_timeframe" && !isSegmentLike) continue;
    // FE rule ids are bookkeeping; regenerating them must not force a CP fork.
    if (key === "id" && isRuleLike) continue;
    if (obj[key] === undefined) continue;
    out[key] = canonicalizePolicyTreeForLineage(obj[key]);
  }
  return out;
}

/**
 * True when two policy trees are the same for Edit & run lineage purposes
 * (date-window / timeframe-only runs must not fork).
 */
export function workspacePolicyTreesEquivalentForLineage(
  a: unknown,
  b: unknown
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  try {
    return (
      JSON.stringify(canonicalizePolicyTreeForLineage(a)) ===
      JSON.stringify(canonicalizePolicyTreeForLineage(b))
    );
  } catch {
    return false;
  }
}

/**
 * Edit & run: fork a derived policy/CP only when the submitted simulate tree
 * differs from the post-hydrate baseline (lineage canonicalize).
 * Do not trust a boolean dirty flag when both trees are present — hydrate
 * churn was marking dirty and forcing forks on unchanged runs.
 */
export function shouldForkDerivedPolicyAfterEditedRun(args: {
  baselinePolicyTree?: unknown;
  submittedPolicyTree?: unknown;
  /**
   * Optional hint when trees are missing. Ignored when both trees are present.
   */
  policyChangedFromBaseline?: boolean;
}): boolean {
  const {
    baselinePolicyTree,
    submittedPolicyTree,
    policyChangedFromBaseline,
  } = args;
  if (baselinePolicyTree != null && submittedPolicyTree != null) {
    return !workspacePolicyTreesEquivalentForLineage(
      baselinePolicyTree,
      submittedPolicyTree
    );
  }
  // Missing trees — only fork when the simulator explicitly says the policy changed.
  return policyChangedFromBaseline === true;
}

/**
 * True when this policy is an Edit & run fork under another policy / combined_policy.
 * Multi-parent combined policies (merge of ≥2 sources) are not forks.
 */
export function workspaceNodeIsPolicyFork(
  node: WorkspaceNode,
  edges: WorkspaceEdge[] = [],
  nodes: WorkspaceNode[] = []
): boolean {
  if (node.type !== "policy" && node.type !== "combined_policy") return false;
  if (node.meta?.isFork || node.meta?.forkedFromEntityId) return true;
  if (edges.length === 0 || nodes.length === 0) return false;
  const nodeById = new Map(nodes.map((entry) => [entry.id, entry]));
  return edges.some((edge) => {
    if (edge.targetNodeId !== node.id) return false;
    const parent = nodeById.get(edge.sourceNodeId);
    if (!parent) return false;
    // policy→policy or combined_policy→combined_policy (Edit & run lineage).
    return parent.type === node.type;
  });
}
